import { DatePickerField } from "@/components";
import InputField from "@/components/InputField";
import { SelectField, SelectOptionType } from "@/components/SelectField";
import {
  BOOLEAN_CHOICE_OPTIONS,
  EQUALITY_COMPARISON_OPTIONS,
  MAIN_OPERATOR,
  NUMERIC_COMPARISON_OPTIONS,
  optionsMOBAction,
  optionsWhen,
  PRIORITY_LEVEL_OPTIONS,
} from "@/partial/campaign/constant";
import { CampaignForm } from "@/partial/campaign/types";
import {
  Control,
  UseFormSetValue,
  UseFormWatch,
  useWatch,
} from "react-hook-form";
import { Fragment } from "react/jsx-runtime";
import { Button, IconDelete } from "tvs-unified-lib/dist";
import { IconAddStyle, ReferOutFlex } from "../../styles";
import {
  AcceptableValidationTypes,
  ActionFieldDisplayTypes,
} from "../../types";

type ConditionFormProps = {
  indexChannel: number;
  index: number;
  control: Control<CampaignForm>;
  watch: UseFormWatch<CampaignForm>;
  setValue: UseFormSetValue<CampaignForm>;
  handleAddReferOut: (index: number, operator: string) => void;
  handleRemoveReferOut: (index: number) => void;
};

export const ConditionForm = ({
  indexChannel,
  index,
  control,
  setValue,
  handleAddReferOut,
  handleRemoveReferOut,
}: ConditionFormProps) => {
  const rowBasePath =
    `campaign_channels[${indexChannel}].refer_out[${index}]` as const;
  const currentOperator = useWatch({
    control,
    name: `${rowBasePath}.current_operator` as `campaign_channels.${number}.refer_out.${number}.current_operator`,
  });
  const whenValue = useWatch({
    control,
    name: `${rowBasePath}.when` as `campaign_channels.${number}.refer_out.${number}.when`,
  });
  const conditionValue = useWatch({
    control,
    name: `${rowBasePath}.condition` as `campaign_channels.${number}.refer_out.${number}.condition`,
  });
  const conditionOptionsForRow = useWatch({
    control,
    name: `${rowBasePath}.condition_options` as `campaign_channels.${number}.refer_out.${number}.condition_options`,
    defaultValue: [],
  });
  const actionDisplayType = useWatch({
    control,
    name: `${rowBasePath}.action_display_type` as `campaign_channels.${number}.refer_out.${number}.action_display_type`,
    defaultValue: ActionFieldDisplayTypes.Disabled,
  });
  const actionValidationType = useWatch({
    control,
    name: `${rowBasePath}.action_validation_type` as `campaign_channels.${number}.refer_out.${number}.action_validation_type`,
    defaultValue: "string",
  });
  const actionOptions = useWatch({
    control,
    name: `${rowBasePath}.action_options` as `campaign_channels.${number}.refer_out.${number}.action_options`,
    defaultValue: [],
  });
  const actionPlaceholder = useWatch({
    control,
    name: `${rowBasePath}.action_placeholder` as `campaign_channels.${number}.refer_out.${number}.action_placeholder`,
    defaultValue: "Action",
  });
  const actionIsDisabled = useWatch({
    control,
    name: `${rowBasePath}.action_is_disabled` as `campaign_channels.${number}.refer_out.${number}.action_is_disabled`,
    defaultValue: true,
  });

  const handleChangeWhen = (selectedWhenOptions: SelectOptionType[]) => {
    const selectedWhenValue = selectedWhenOptions[0]?.value;
    // reset condition options
    setValue(
      `${rowBasePath}.condition` as `campaign_channels.${number}.refer_out.${number}.condition`,
      []
    );
    // reset action type
    setValue(
      `${rowBasePath}.action_type_numeric` as `campaign_channels.${number}.refer_out.${number}.action_type_numeric`,
      ""
    );
    setValue(
      `${rowBasePath}.action_type_boolean` as `campaign_channels.${number}.refer_out.${number}.action_type_boolean`,
      [] as SelectOptionType[]
    );
    setValue(
      `${rowBasePath}.action_type_string` as `campaign_channels.${number}.refer_out.${number}.action_type_string`,
      ""
    );
    setValue(
      `${rowBasePath}.action_type_date` as `campaign_channels.${number}.refer_out.${number}.action_type_date`,
      ""
    );
    setValue(
      `${rowBasePath}.action_type_dropdown` as `campaign_channels.${number}.refer_out.${number}.action_type_dropdown`,
      [] as SelectOptionType[]
    );

    let newConditionOptions: SelectOptionType[] = [];
    let newActionDisplayType: ActionFieldDisplayTypes =
      ActionFieldDisplayTypes.Disabled;
    let newActionValidationType: AcceptableValidationTypes = "string";
    let newActionOptions: SelectOptionType[] = [];
    let newActionPlaceholder: string = "Action";
    let newActionIsDisabled: boolean = true;

    switch (selectedWhenValue) {
      case "Bucket":
        newConditionOptions = NUMERIC_COMPARISON_OPTIONS;
        newActionDisplayType = ActionFieldDisplayTypes.Input;
        newActionValidationType = "numeric";
        newActionOptions = [];
        newActionPlaceholder = "Action";
        newActionIsDisabled = false;
        break;
      case "Past Due Date":
        newConditionOptions = NUMERIC_COMPARISON_OPTIONS;
        newActionDisplayType = ActionFieldDisplayTypes.Input;
        newActionValidationType = "date";
        newActionOptions = [];
        newActionPlaceholder = "Action";
        newActionIsDisabled = false;
        break;
      case "Customer Risk Factor":
        newConditionOptions = PRIORITY_LEVEL_OPTIONS;
        newActionDisplayType = ActionFieldDisplayTypes.Input;
        newActionValidationType = "string";
        newActionOptions = [];
        newActionPlaceholder = "Action";
        newActionIsDisabled = true;
        break;
      case "Current Month Bounce":
        newConditionOptions = BOOLEAN_CHOICE_OPTIONS;
        newActionDisplayType = ActionFieldDisplayTypes.Input;
        newActionValidationType = "string";
        newActionOptions = [];
        newActionPlaceholder = "Action";
        newActionIsDisabled = true;
        break;
      case "Overdue Amount":
        newConditionOptions = NUMERIC_COMPARISON_OPTIONS;
        newActionDisplayType = ActionFieldDisplayTypes.Input;
        newActionValidationType = "numeric";
        newActionOptions = [];
        newActionPlaceholder = "Action";
        newActionIsDisabled = false;
        break;
      case "MOB":
        newConditionOptions = EQUALITY_COMPARISON_OPTIONS;
        newActionDisplayType = ActionFieldDisplayTypes.Dropdown;
        newActionValidationType = "numeric";
        newActionOptions = optionsMOBAction;
        newActionPlaceholder = "Action";
        newActionIsDisabled = false;
        break;
      case "First EMI":
      case "Second EMI":
        newConditionOptions = BOOLEAN_CHOICE_OPTIONS;
        newActionDisplayType = ActionFieldDisplayTypes.Dropdown;
        newActionValidationType = "string";
        newActionOptions = [];
        newActionPlaceholder = "Action";
        newActionIsDisabled = true;
        break;
      default:
        newConditionOptions = [];
        break;
    }

    setValue(
      `${rowBasePath}.condition_options` as `campaign_channels.${number}.refer_out.${number}.condition_options`,
      newConditionOptions
    );
    setValue(
      `${rowBasePath}.action_display_type` as `campaign_channels.${number}.refer_out.${number}.action_display_type`,
      newActionDisplayType
    );
    setValue(
      `${rowBasePath}.action_validation_type` as `campaign_channels.${number}.refer_out.${number}.action_validation_type`,
      newActionValidationType
    );
    setValue(
      `${rowBasePath}.action_options` as `campaign_channels.${number}.refer_out.${number}.action_options`,
      newActionOptions
    );
    setValue(
      `${rowBasePath}.action_placeholder` as `campaign_channels.${number}.refer_out.${number}.action_placeholder`,
      newActionPlaceholder
    );
    setValue(
      `${rowBasePath}.action_is_disabled` as `campaign_channels.${number}.refer_out.${number}.action_is_disabled`,
      newActionIsDisabled
    );
  };

  const renderActionField = (
    actionDisplayType: ActionFieldDisplayTypes,
    actionValidationType: AcceptableValidationTypes,
    actionOptions: SelectOptionType[],
    actionPlaceholder: string,
    actionIsDisabled: boolean
  ) => {
    switch (actionDisplayType) {
      case ActionFieldDisplayTypes.Dropdown:
        return (
          <SelectField
            name={`${rowBasePath}.action_type_dropdown`}
            placeholder={actionPlaceholder}
            control={control}
            options={actionOptions}
            disabled={actionIsDisabled ? true : conditionValue?.length === 0}
            isRequired
          />
        );
      case ActionFieldDisplayTypes.Input:
        if (actionValidationType === "numeric") {
          return (
            <InputField
              name={`${rowBasePath}.action_type_numeric`}
              placeholder={actionPlaceholder}
              control={control}
              type="number"
              disabled={actionIsDisabled ? true : conditionValue?.length === 0}
              isRequired
            />
          );
        }
        if (actionValidationType === "date") {
          return (
            <DatePickerField
              name={`${rowBasePath}.action_type_date`}
              placeholder={actionPlaceholder}
              control={control}
              disabled={actionIsDisabled ? true : conditionValue?.length === 0}
              isRequired
            />
          );
        }
        if (actionValidationType === "boolean") {
          return (
            <SelectField
              name={`${rowBasePath}.action_type_boolean`}
              placeholder={actionPlaceholder}
              control={control}
              options={BOOLEAN_CHOICE_OPTIONS}
              disabled={actionIsDisabled ? true : conditionValue?.length === 0}
              isRequired
            />
          );
        }
        if (actionValidationType === "string") {
          return (
            <InputField
              name={`${rowBasePath}.action_type_string`}
              placeholder={actionPlaceholder}
              control={control}
              disabled={actionIsDisabled ? true : conditionValue?.length === 0}
              isRequired
            />
          );
        }
        return null;
      case ActionFieldDisplayTypes.Disabled:
        return (
          <InputField
            name={`${rowBasePath}.action_type_string`}
            placeholder={actionPlaceholder}
            control={control}
            disabled
            isRequired
          />
        );
      default:
        return null;
    }
  };

  return (
    <Fragment>
      <ReferOutFlex>
        <SelectField
          name={`${rowBasePath}.when`}
          isRequired
          placeholder="When"
          control={control}
          options={optionsWhen} // These are global options
          onChange={(e) => handleChangeWhen(e)}
        />
        <SelectField
          name={`${rowBasePath}.condition`}
          isRequired
          placeholder="Condition"
          control={control}
          options={conditionOptionsForRow}
          disabled={whenValue?.length === 0}
        />
        {renderActionField(
          actionDisplayType as ActionFieldDisplayTypes,
          actionValidationType as AcceptableValidationTypes,
          actionOptions as SelectOptionType[],
          actionPlaceholder as string,
          actionIsDisabled as boolean
        )}
        <Button
          iconOnly
          variant="outline"
          className="button-add"
          icon={<IconAddStyle />}
          onClick={() => handleAddReferOut(index, MAIN_OPERATOR.AND)}
        />
        <Button
          iconOnly
          variant="outline"
          className="button-delete"
          icon={<IconDelete />}
          disabled={index === 0}
          onClick={() => handleRemoveReferOut(index)}
        />
      </ReferOutFlex>
      {currentOperator === MAIN_OPERATOR.OR && (
        <ReferOutFlex>
          <Button
            size="md"
            variant="outline"
            label="Or"
            className="button-or"
            onClick={() => handleAddReferOut(index, MAIN_OPERATOR.OR)}
          />
        </ReferOutFlex>
      )}
    </Fragment>
  );
};

export default ConditionForm;
